import { useState, useEffect, useContext } from 'react';
import { AppContext } from '../../context';
import api from '../../hooks/useApi';
import { parseRoles } from '../../utils/rbac';

const USD_TO_GBP = 0.79;
const fmt = (n, dp = 2) => n != null ? `£${(Number(n) * USD_TO_GBP).toFixed(dp)}` : '—';
const fmtUsd = (n, dp = 4) => n != null ? `$${Number(n).toFixed(dp)}` : '—';
const fmtNum = (n) => n != null ? Number(n).toLocaleString() : '—';

function prevMonth(m) {
  const [y, mo] = m.split('-').map(Number);
  return mo === 1 ? `${y - 1}-12` : `${y}-${String(mo - 1).padStart(2, '0')}`;
}
function nextMonth(m) {
  const [y, mo] = m.split('-').map(Number);
  return mo === 12 ? `${y + 1}-01` : `${y}-${String(mo + 1).padStart(2, '0')}`;
}
function fmtMonth(m) {
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

export default function AIUsageReportPage() {
  const { currentUser } = useContext(AppContext);
  const roles = parseRoles(currentUser?.user_role);
  const canView = roles.some(r => ['admin', 'pmo'].includes(r));

  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState([]);
  const [prevSummary, setPrevSummary] = useState([]);
  const [detail, setDetail] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!canView) return;
    setLoading(true);
    Promise.all([
      api.get('ai-usage/summary', { month }),
      api.get('ai-usage/summary', { month: prevMonth(month) }),
      api.get('ai-usage', { month }),
    ]).then(([sum, prev, det]) => {
      setSummary(Array.isArray(sum) ? sum : []);
      setPrevSummary(Array.isArray(prev) ? prev : []);
      setDetail(Array.isArray(det) ? det : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [month, canView]);

  if (!canView) return (
    <div className="fade-in flex items-center justify-center h-64 text-gray-400 text-sm">
      Access restricted to Admin and PMO roles.
    </div>
  );

  const totalCost = summary.reduce((s, r) => s + (r.total_cost_usd || 0), 0);
  const totalCalls = summary.reduce((s, r) => s + (r.call_count || 0), 0);
  const totalTokens = summary.reduce((s, r) => s + (r.total_input_tokens || 0) + (r.total_output_tokens || 0), 0);
  const prevTotalCost = prevSummary.reduce((s, r) => s + (r.total_cost_usd || 0), 0);
  const costChange = prevTotalCost > 0 ? ((totalCost - prevTotalCost) / prevTotalCost) * 100 : null;

  // Feature breakdown from detailed logs
  const byFeature = detail.reduce((acc, r) => {
    const k = r.feature || 'unknown';
    if (!acc[k]) acc[k] = { calls: 0, tokens: 0, cost: 0 };
    acc[k].calls++;
    acc[k].tokens += (r.input_tokens || 0) + (r.output_tokens || 0);
    acc[k].cost += r.cost_usd || 0;
    return acc;
  }, {});
  const featureRows = Object.entries(byFeature).sort((a, b) => b[1].cost - a[1].cost);

  const today = new Date().toISOString().slice(0, 7);

  return (
    <div className="fade-in">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-extrabold text-navy">AI Usage Report</h2>
          <div className="text-[9px] text-gray-500">Cost and usage breakdown across all staff</div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setMonth(prevMonth(month))}
            className="px-2 py-1 border border-gray-200 rounded text-[10px] hover:bg-gray-50">‹</button>
          <span className="text-[11px] font-semibold text-navy w-36 text-center">{fmtMonth(month)}</span>
          <button onClick={() => setMonth(nextMonth(month))} disabled={month >= today}
            className="px-2 py-1 border border-gray-200 rounded text-[10px] hover:bg-gray-50 disabled:opacity-30">›</button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {[
          {
            label: 'Total Cost (GBP)',
            value: fmt(totalCost, 2),
            sub: fmtUsd(totalCost, 4),
            cls: 'text-navy',
            trend: costChange,
          },
          {
            label: 'AI Calls',
            value: fmtNum(totalCalls),
            sub: `${summary.length} staff member${summary.length !== 1 ? 's' : ''}`,
            cls: 'text-blue-700',
          },
          {
            label: 'Total Tokens',
            value: fmtNum(totalTokens),
            sub: totalCalls > 0 ? `avg ${fmtNum(Math.round(totalTokens / totalCalls))}/call` : null,
            cls: 'text-purple-700',
          },
          {
            label: 'Avg Cost / Person',
            value: summary.length > 0 ? fmt(totalCost / summary.length, 2) : '—',
            sub: `across ${summary.length} active users`,
            cls: 'text-emerald-700',
          },
        ].map(card => (
          <div key={card.label} className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-[8px] text-gray-400 uppercase tracking-wide mb-1">{card.label}</div>
            <div className={`text-xl font-extrabold ${card.cls}`}>{card.value}</div>
            {card.sub && <div className="text-[8px] text-gray-400 mt-0.5">{card.sub}</div>}
            {card.trend != null && (
              <div className={`text-[8px] font-semibold mt-1 ${card.trend > 0 ? 'text-red-500' : 'text-emerald-600'}`}>
                {card.trend > 0 ? '▲' : '▼'} {Math.abs(card.trend).toFixed(1)}% vs {fmtMonth(prevMonth(month))}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Per-person table */}
        <div className="col-span-2 bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50 flex items-center justify-between">
            <span className="text-[10px] font-bold text-navy">Cost by Person</span>
            <span className="text-[8px] text-gray-400">{summary.length} users · {fmtMonth(month)}</span>
          </div>
          {loading ? (
            <div className="p-6 text-center text-[10px] text-gray-400">Loading…</div>
          ) : summary.length === 0 ? (
            <div className="p-6 text-center text-[10px] text-gray-400">No AI usage recorded for {fmtMonth(month)}</div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-400 uppercase w-6">#</th>
                  <th className="px-3 py-2 text-left text-[8px] font-bold text-gray-400 uppercase">Person</th>
                  <th className="px-2 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Calls</th>
                  <th className="px-2 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Tokens</th>
                  <th className="px-3 py-2 text-right text-[8px] font-bold text-gray-400 uppercase">Cost (GBP)</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((r, i) => {
                  const tokens = (r.total_input_tokens || 0) + (r.total_output_tokens || 0);
                  const pct = totalCost > 0 ? (r.total_cost_usd / totalCost) * 100 : 0;
                  return (
                    <tr key={r.person_id} className="border-b border-gray-50 hover:bg-gray-50/50">
                      <td className="px-3 py-2 text-[9px] text-gray-300">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="text-[10px] font-semibold text-gray-800">{r.person_name}</div>
                        <div className="h-1 bg-gray-100 rounded mt-1 w-24">
                          <div className="h-1 bg-navy rounded" style={{ width: `${pct}%` }} />
                        </div>
                      </td>
                      <td className="px-2 py-2 text-right text-[9px] text-gray-600">{r.call_count}</td>
                      <td className="px-2 py-2 text-right text-[9px] text-gray-600">{fmtNum(tokens)}</td>
                      <td className="px-3 py-2 text-right text-[10px] font-bold text-navy">{fmt(r.total_cost_usd, 4)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td colSpan={2} className="px-3 py-2 text-[9px] font-bold text-gray-600">Total</td>
                  <td className="px-2 py-2 text-right text-[9px] font-bold text-gray-700">{totalCalls}</td>
                  <td className="px-2 py-2 text-right text-[9px] font-bold text-gray-700">{fmtNum(totalTokens)}</td>
                  <td className="px-3 py-2 text-right text-[10px] font-extrabold text-navy">{fmt(totalCost, 4)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </div>

        {/* Feature breakdown */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="px-3 py-2 border-b border-gray-100 bg-gray-50">
            <span className="text-[10px] font-bold text-navy">By Feature</span>
          </div>
          {featureRows.length === 0 ? (
            <div className="p-4 text-center text-[10px] text-gray-400">No data</div>
          ) : (
            <div className="divide-y divide-gray-50">
              {featureRows.map(([feature, stats]) => (
                <div key={feature} className="px-3 py-2">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[9px] font-semibold text-gray-700 capitalize">{feature.replace(/_/g, ' ')}</span>
                    <span className="text-[9px] font-bold text-navy">{fmt(stats.cost, 4)}</span>
                  </div>
                  <div className="flex items-center gap-2 text-[8px] text-gray-400">
                    <span>{stats.calls} calls</span>
                    <span>·</span>
                    <span>{fmtNum(stats.tokens)} tokens</span>
                  </div>
                  <div className="h-1 bg-gray-100 rounded mt-1">
                    <div className="h-1 bg-blue-400 rounded"
                      style={{ width: `${totalCost > 0 ? (stats.cost / totalCost) * 100 : 0}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
